import { useState } from "react";
import { Table, Tag, Input, Card, Tabs } from "antd";
import { Search, ShieldAlert } from "lucide-react";
import { useComplianceOverview } from "../../../api-hooks/companies";
import dayjs from "dayjs";

function expiryTag(dateStr: string | null) {
  if (!dateStr) return <span className="text-slate-400">—</span>;
  const d = dayjs(dateStr);
  const diff = d.diff(dayjs(), "day");
  if (diff < 0) return <Tag color="red">EXPIRED ({d.format("DD/MM/YY")})</Tag>;
  if (diff <= 14) return <Tag color="volcano">{d.format("DD/MM/YY")} ({diff}d)</Tag>;
  if (diff <= 30) return <Tag color="orange">{d.format("DD/MM/YY")} ({diff}d)</Tag>;
  return <Tag color="gold">{d.format("DD/MM/YY")} ({diff}d)</Tag>;
}

export default function PlatformCompliancePage() {
  const [search, setSearch] = useState("");
  const { data, isLoading } = useComplianceOverview();
  const dbs: any[] = data?.dbs ?? [];
  const rtw: any[] = data?.rtw ?? [];

  const filterRows = (rows: any[]) =>
    rows.filter((r) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return r.worker_name?.toLowerCase().includes(q) || r.company_name?.toLowerCase().includes(q);
    });

  const dbsExpired = dbs.filter((r) => r.expiry_date && dayjs(r.expiry_date).isBefore(dayjs())).length;
  const rtwExpired = rtw.filter((r) => r.expiry_date && dayjs(r.expiry_date).isBefore(dayjs())).length;

  const dbsCols = [
    {
      title: "Worker",
      dataIndex: "worker_name",
      key: "worker_name",
      render: (v: string) => <span className="font-medium text-slate-800">{v || "—"}</span>,
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      render: (v: string) => <span className="text-sm font-medium text-violet-700">{v}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => {
        const c: Record<string, string> = { clear: "green", enhanced: "blue", basic: "default", expired: "red" };
        return <Tag color={c[v] || "default"}>{v}</Tag>;
      },
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (v: string | null) => expiryTag(v),
      sorter: (a: any, b: any) => {
        if (!a.expiry_date) return 1;
        if (!b.expiry_date) return -1;
        return dayjs(a.expiry_date).unix() - dayjs(b.expiry_date).unix();
      },
      defaultSortOrder: "ascend" as const,
    },
  ];

  const rtwCols = [
    {
      title: "Worker",
      dataIndex: "worker_name",
      key: "worker_name",
      render: (v: string) => <span className="font-medium text-slate-800">{v || "—"}</span>,
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      render: (v: string) => <span className="text-sm font-medium text-violet-700">{v}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => {
        const c: Record<string, string> = { verified: "green", pending: "orange", expired: "red", failed: "red" };
        return <Tag color={c[v] || "default"}>{v}</Tag>;
      },
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (v: string | null) => expiryTag(v),
      sorter: (a: any, b: any) => {
        if (!a.expiry_date) return 1;
        if (!b.expiry_date) return -1;
        return dayjs(a.expiry_date).unix() - dayjs(b.expiry_date).unix();
      },
      defaultSortOrder: "ascend" as const,
    },
  ];

  const filteredDbs = filterRows(dbs);
  const filteredRtw = filterRows(rtw);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldAlert size={20} className="text-violet-700" />
            <h2 className="text-xl font-semibold text-slate-800">Platform Compliance Overview</h2>
          </div>
          <p className="text-slate-500 text-sm">DBS checks and Right to Work expiring within 90 days — across all companies</p>
        </div>
        <div className="flex gap-4">
          {dbsExpired > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              <span className="text-red-600 font-semibold text-sm">{dbsExpired} Expired DBS</span>
            </div>
          )}
          {rtwExpired > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              <span className="text-red-600 font-semibold text-sm">{rtwExpired} Expired RTW</span>
            </div>
          )}
        </div>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm mb-4">
        <Input
          prefix={<Search size={14} className="text-slate-400" />}
          placeholder="Search by worker or company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg"
          style={{ maxWidth: 360 }}
          allowClear
        />
      </Card>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        <Tabs
          className="px-4"
          items={[
            {
              key: "dbs",
              label: `DBS Checks (${filteredDbs.length})`,
              children: (
                <Table
                  dataSource={filteredDbs}
                  columns={dbsCols}
                  rowKey="id"
                  loading={isLoading}
                  size="small"
                  pagination={{ pageSize: 30, showTotal: (t) => `${t} records` }}
                  rowClassName={(r) => r.expiry_date && dayjs(r.expiry_date).isBefore(dayjs()) ? "bg-red-50" : ""}
                />
              ),
            },
            {
              key: "rtw",
              label: `Right to Work (${filteredRtw.length})`,
              children: (
                <Table
                  dataSource={filteredRtw}
                  columns={rtwCols}
                  rowKey="id"
                  loading={isLoading}
                  size="small"
                  pagination={{ pageSize: 30, showTotal: (t) => `${t} records` }}
                  rowClassName={(r) => r.expiry_date && dayjs(r.expiry_date).isBefore(dayjs()) ? "bg-red-50" : ""}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
